import { useState } from 'react';
import { Alert, Badge, Button, Group, NumberInput, Paper, Select, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { PageHeader } from '../../../components/PageHeader';
import { useActiveProducts, useAllProducts, useCreateAndPostPackConversion, useLocations, usePackSpec, useSites } from '../api';
import { formatLineQty, formatMoneyCents, formatQty, nowForDatetimeLocal } from '../format';
import { PackSpecPanel } from './PackSpecPanel';

/** Pack conversion guided flow (BUILD_SPEC.md §8.5): pick a pack spec (via its
 * pack product) + number of packs + location -> preview (bulk mass + packaging
 * consumed -> pack units produced, value conserved) -> Post. Create+post is one
 * composite mutation (see api.ts's `useCreateAndPostPackConversion` header for
 * why two separately-instantiated hooks would race on a stale id). */
export function PackConversionFlow() {
  const canOperate = useCanOperate();
  const [packProductId, setPackProductId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  // Mantine's NumberInput (react-number-format) calls onChange with the RAW
  // in-progress STRING instead of a parsed number whenever the typed value
  // still matches its trailingZerosPattern — so state accepts `number |
  // string` and onChange passes the value through unaltered; `Number(numPacks)`
  // below (canPost/handlePost) already tolerates a numeric string.
  const [numPacks, setNumPacks] = useState<number | string>('');
  const [occurredAt, setOccurredAt] = useState(nowForDatetimeLocal());

  // `products` (active only) feeds the pack-product PICKER; `allProducts` feeds
  // the LOOKUPS (bulk product's name/baseUom, packaging names in the preview) —
  // a spec's bulk/packaging product may have been deactivated since the spec was
  // saved, and the preview must still resolve it (see api.ts's useAllProducts doc).
  const productsQuery = useActiveProducts();
  const allProductsQuery = useAllProducts();
  const sitesQuery = useSites();
  const locationsQuery = useLocations(siteId ?? undefined);
  const specQuery = usePackSpec(packProductId);
  const createAndPost = useCreateAndPostPackConversion();

  const products = productsQuery.data?.items ?? [];
  const allProducts = allProductsQuery.data?.items ?? [];
  const spec = specQuery.data;
  const bulkProduct = allProducts.find((p) => p.id === spec?.bulkProductId);
  const packProduct = allProducts.find((p) => p.id === packProductId);

  const canPost = Boolean(spec) && Boolean(locationId) && numPacks !== '' && Number(numPacks) > 0;

  function reset() {
    setPackProductId(null);
    setSiteId(null);
    setLocationId(null);
    setNumPacks('');
    setOccurredAt(nowForDatetimeLocal());
    createAndPost.reset();
  }

  function handlePost() {
    if (!spec || !locationId || numPacks === '') return;
    createAndPost.mutate({
      packSpecId: spec.id,
      numPacks: Number(numPacks),
      locationId,
      occurredAt: new Date(occurredAt),
      notes: null,
    });
  }

  if (createAndPost.isSuccess && createAndPost.data) {
    const { post } = createAndPost.data;
    return (
      <Paper withBorder radius="md" p="xl" maw={640}>
        <Stack gap="md" align="flex-start">
          <Badge color="green" size="lg" leftSection={<IconCheck size={14} />}>
            Posted
          </Badge>
          <Title order={2}>Pack conversion complete</Title>
          <Text>{post.summary}</Text>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Tbody>
                {post.movements.map((m) => (
                  <Table.Tr key={m.movementId}>
                    <Table.Td>{m.type}</Table.Td>
                    <Table.Td>{formatQty(m.qty)}</Table.Td>
                    <Table.Td>{formatMoneyCents(m.valueCents)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Button onClick={reset}>Convert another pack</Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader title="Pack conversion" description="Convert bulk product into a packaged SKU and post the stock movement." />
      <Stack gap="sm" maw={520}>
        <Select
          label="Pack product"
          placeholder="Choose the pack SKU to produce"
          data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
          value={packProductId}
          onChange={(v) => {
            setPackProductId(v);
            createAndPost.reset();
          }}
          searchable
        />
      </Stack>

      {packProductId && (
        <Paper withBorder radius="md" p="md" maw={640}>
          <Title order={4} mb="xs">
            Pack spec — {packProduct?.name ?? packProductId}
          </Title>
          <PackSpecPanel packProductId={packProductId} />
        </Paper>
      )}

      {packProductId && spec && (
        <Stack gap="sm" maw={520}>
          <Select
            label="Site"
            data={(sitesQuery.data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
            value={siteId}
            onChange={(v) => {
              setSiteId(v);
              setLocationId(null);
            }}
            searchable
          />
          <Select
            label="Location"
            placeholder="Where the bagging/boxing happens"
            data={(locationsQuery.data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
            value={locationId}
            onChange={setLocationId}
            searchable
            disabled={!siteId}
          />
          <NumberInput
            label="Number of packs"
            decimalScale={0}
            min={1}
            value={numPacks}
            onChange={setNumPacks}
          />
          <TextInput label="Occurred at" type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.currentTarget.value)} />
        </Stack>
      )}

      {packProductId && spec && locationId && numPacks !== '' && Number(numPacks) > 0 && (
        <Alert color="green" variant="light" title="Ledger effect preview" maw={640}>
          <Stack gap={4}>
            <Text size="sm">
              {/* Unit-aware — the bulk product consumed per pack is not always
                  mass-tracked (e.g. firelighter EACH units bulk-packed into a
                  box); see PackSpecPanel's identical comment. */}
              Bulk consumed:{' '}
              <strong>{formatLineQty((Number(numPacks) * Number(spec.qtyPerPack)).toFixed(3), bulkProduct?.baseUom)}</strong> of{' '}
              {bulkProduct?.name ?? spec.bulkProductId}
            </Text>
            {(spec.packaging ?? []).map((p) => (
              <Text size="sm" key={p.productId}>
                Packaging consumed: <strong>{formatQty((Number(numPacks) * Number(p.qty)).toFixed(3))}</strong> of{' '}
                {allProducts.find((pr) => pr.id === p.productId)?.name ?? p.productId}
              </Text>
            ))}
            <Text size="sm">
              Produced: <strong>{numPacks} unit(s)</strong> of {packProduct?.name ?? packProductId} — value carried forward from
              everything consumed above (conserved to the cent).
            </Text>
          </Stack>
        </Alert>
      )}

      {createAndPost.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} maw={640}>
          {createAndPost.error instanceof ApiRequestError ? createAndPost.error.message : 'Failed to post the pack conversion'}
        </Alert>
      )}

      {packProductId && spec && (
        <Group maw={640} justify="flex-end">
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button size="lg" disabled={!canOperate || !canPost} loading={createAndPost.isPending} onClick={handlePost}>
                Post
              </Button>
            </span>
          </Tooltip>
        </Group>
      )}
    </Stack>
  );
}
